import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  Textarea,
  Checkbox,
  FormLabel,
  useToast,
  SimpleGrid,
  Divider,
  Box,
  Flex,
  Text
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { OficialiaFormulario } from './types';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  modo: 'view' | 'edit' | 'new';
  data?: OficialiaFormulario | null;
  onGuardar: () => void;
}

const ModalOficialia: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  modo,
  data,
  onGuardar,
}) => {
  const [form, setForm] = useState<Partial<OficialiaFormulario>>({});
  const [archivo, setArchivo] = useState<File | null>(null);
  const toast = useToast();
  const isReadOnly = modo === 'view';

  useEffect(() => {
  if (data) {
    // Convertir fechaRecepcion a formato local sin segundos ni zona
    const fechaRecepcionFormateada = data.fechaRecepcion
      ? new Date(data.fechaRecepcion).toISOString().slice(0, 16)
      : '';

    setForm({
      ...data,
      fechaRecepcion: fechaRecepcionFormateada,
      horaRecepcion: data.horaRecepcion || '', // asegúrate de que no sea undefined
    });
  } else {
    // Si es nuevo
    const now = new Date();
    const fechaActual = now.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm

    setForm({
      remitente: '',
      dependencia: '',
      esParticular: false,
      correo: '',
      celular: '',
      tipoDocumento: '',
      asunto: '',
      fechaRecepcion: fechaActual,
      horaRecepcion: '',
      estado: 'Recibido',
      archivo: null,
    });
  }
}, [data]);


  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setArchivo(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (
      !form.remitente ||
      !form.tipoDocumento ||
      !form.asunto ||
      !form.fechaRecepcion
    ) {
      toast({
        title: 'Faltan datos obligatorios.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    if (archivo) {
      formData.append('archivo', archivo);
    }

    const metodo = modo === 'edit' ? 'PUT' : 'POST';
    const url =
      modo === 'edit'
        ? `https://totolac-backend.togui.com.mx/api/oficialia/${data?.id}`
        : 'https://totolac-backend.togui.com.mx/api/oficialia';

    try {
      await fetch(url, {
        method: metodo,
        body: formData,
      });

      toast({ title: 'Registro guardado.', status: 'success', duration: 3000 });
      onClose();
      onGuardar();
    } catch (error) {
      toast({ title: 'Error al guardar.', status: 'error', duration: 3000 });
    }
  };

  useEffect(() => {
    if (data) {
      setForm(data);
    } else {
      const now = new Date();
      const fechaActual = now.toISOString().slice(0, 16); // formato "YYYY-MM-DDTHH:mm"
      setForm({
        remitente: '',
        dependencia: '',
        esParticular: false,
        correo: '',
        celular: '',
        tipoDocumento: '',
        asunto: '',
        fechaRecepcion: fechaActual,
        horaRecepcion: '',
        estado: 'Recibido',
        archivo: null,
      });
    }
  }, [data]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="white"
          textShadow="2px 2px 4px rgba(0,0,0,0.7)"
          fontWeight="bold"
          position="relative"
        >
          {modo === 'new'
            ? 'Nueva Entrada de Oficialía'
            : modo === 'edit'
            ? 'Editar Entrada'
            : 'Ver Entrada'}
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
        <ModalBody>
            <Flex direction={['column', 'row']} gap={6}>
    {/* FORMULARIO */}
    <Box flex="2">
          <SimpleGrid columns={[1, 1]} spacing={3}>
            <div style={{ gridColumn: '1 / -1' }}>
              <FormLabel fontSize="lg" fontWeight="bold">
                Información del Solicitante
              </FormLabel>
              <Divider mb={2} />
            </div>

            <div>
              <FormLabel>Remitente</FormLabel>
              <Input
                name="remitente"
                value={form.remitente || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </div>

            <div>
              <FormLabel>Dependencia</FormLabel>
              <Input
                name="dependencia"
                value={form.dependencia || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </div>

            <div>
              <FormLabel>¿Es Particular?</FormLabel>
              <Checkbox
                name="esParticular"
                isChecked={form.esParticular || false}
                onChange={handleChange}
                isDisabled={isReadOnly}
              >
                Marcar si es particular
              </Checkbox>
            </div>
          </SimpleGrid>
          <SimpleGrid columns={[1, 2]} spacing={3}>
            <div>
              <FormLabel>Correo</FormLabel>
              <Input
                name="correo"
                value={form.correo || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </div>

            <div>
              <FormLabel>Celular</FormLabel>
              <Input
                name="celular"
                value={form.celular || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </div>
          </SimpleGrid>
          <SimpleGrid columns={[1, 3]} spacing={3}>
            <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
              <FormLabel fontSize="lg" fontWeight="bold">
                Documento y Recepción
              </FormLabel>
              <Divider mb={2} />
            </div>

            <div>
              <FormLabel>Tipo de Documento</FormLabel>
              <Select
                name="tipoDocumento"
                value={form.tipoDocumento || ''}
                onChange={handleChange}
                isDisabled={isReadOnly}
              >
                <option value="">Seleccione</option>
                <option value="Oficio">Oficio</option>
                <option value="Solicitud">Solicitud</option>
                <option value="Carta">Carta</option>
                <option value="Expediente">Expediente</option>
                <option value="Otro">Otro</option>
              </Select>
            </div>

            <div>
              <FormLabel>Estado</FormLabel>
              <Select
                name="estado"
                value={form.estado || 'Recibido'}
                onChange={handleChange}
                isDisabled={isReadOnly}
              >
                <option value="Recibido">Recibido</option>
                <option value="En proceso">En proceso</option>
                <option value="En validación">En validación</option>
                <option value="Supervisión">Supervisión</option>
                <option value="Archivado">Archivado</option>
              </Select>
            </div>

            <div>
              <FormLabel>Fecha y Hora de Recepción</FormLabel>
           <Input
  type="datetime-local"
  name="fechaRecepcion"
  value={
    form.fechaRecepcion
      ? new Date(form.fechaRecepcion).toISOString().slice(0, 16)
      : ''
  }
  onChange={handleChange}
  isReadOnly={isReadOnly}
/>

            </div>
          </SimpleGrid>
          <SimpleGrid columns={[1, 1]} spacing={3}>
            <div>
              <FormLabel>Asunto</FormLabel>
              <Textarea
                name="asunto"
                value={form.asunto || ''}
                onChange={handleChange}
                isReadOnly={isReadOnly}
              />
            </div>

            <div style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
              <FormLabel fontSize="lg" fontWeight="bold">
                Evidencia
              </FormLabel>
              <Divider mb={2} />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <FormLabel>Documento Adjunto</FormLabel>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                isDisabled={isReadOnly}
              />
            </div>
          </SimpleGrid>
          </Box>
  {/* VISTA DEL ARCHIVO PDF */}
      <Box
    mt={4}
    w="50%"
    h="800px"
    border="1px solid #ccc"
    borderRadius="md"
    overflow="hidden"
  >
    <iframe
      src={`https://totolac-backend.togui.com.mx/uploads/oficialia/${form.archivo}`}
      title="Documento Adjunto"
      width="100%"
      height="100%"
      style={{ border: 'none' }}
    />
  </Box>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose} mr={3}>
            Cancelar
          </Button>
          {modo !== 'view' && (
            <Button bg="#5b182b" color="white" _hover={{ bg: "#8a1f35" }} onClick={handleSubmit}>
              Guardar
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalOficialia;
